import { addDays, format, isSameDay, isToday, isWeekend, startOfWeek } from "date-fns";
import { useMemo } from "react";
import { cn } from "@utils";
import EventChip from "./EventChip";

// Tints a day column: today is anchored, weekends recede — the header cell and the column below share
// it so each day reads as one continuous strip.
const columnTint = (day) =>
  isToday(day) ? "bg-primary-50/50" : isWeekend(day) ? "bg-zinc-50/70" : "";

// The Week view is a seven-column agenda: each day stacks its scheduled work and the
// merchant's own events as compact chips — the same chip the Month view uses, so the two
// read identically. It is not an hour grid — each chip carries its own time, which reads
// better for the handful of items a day usually holds.
const WeekView = ({ viewDate, items, onSelectItem, onCreateAt, weekdaysOnly = false }) => {
  const days = useMemo(() => {
    const weekStart = startOfWeek(viewDate, { weekStartsOn: 1 });
    const all = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    return weekdaysOnly ? all.filter((day) => !isWeekend(day)) : all;
  }, [viewDate, weekdaysOnly]);

  const columnsStyle = { gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` };

  const anchorOf = (item) => item.start ?? item.due ?? null;
  const byTime = (a, b) => (anchorOf(a)?.getTime() ?? 0) - (anchorOf(b)?.getTime() ?? 0);

  // A day's items, chronological — projections and the merchant's own events together, each
  // rendered as its own chip (no per-type grouping), matching the Month view.
  const itemsFor = (day) =>
    items.filter((item) => anchorOf(item) && isSameDay(anchorOf(item), day)).sort(byTime);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
      <div className="grid shrink-0 border-b border-zinc-200" style={columnsStyle}>
        {days.map((day) => (
          <div
            key={day.toISOString()}
            className={cn(
              "flex items-center justify-center gap-1.5 border-l border-zinc-100 px-2 py-2 first:border-l-0",
              columnTint(day),
            )}
          >
            <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              {format(day, "EEE")}
            </span>
            <span
              className={cn(
                "flex size-6 items-center justify-center rounded-full text-sm",
                isToday(day) ? "bg-primary-500 font-semibold text-white" : "text-zinc-700",
              )}
            >
              {format(day, "d")}
            </span>
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 overflow-y-auto" style={columnsStyle}>
        {days.map((day) => (
          <div
            key={day.toISOString()}
            onClick={() => {
              const at = new Date(day);
              at.setHours(9, 0, 0, 0);
              onCreateAt?.(at);
            }}
            className={cn(
              "min-w-0 cursor-pointer space-y-1 border-l border-zinc-100 p-1.5 transition-colors first:border-l-0 hover:bg-zinc-50/60",
              columnTint(day),
            )}
          >
            {itemsFor(day).map((item) => (
              <EventChip
                key={item.occurrenceKey ?? item.uid}
                item={item}
                onClick={onSelectItem}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeekView;
